/**
 * The task pocket's Runs (mockup 2026-10-29 project wall, "Task open"): one
 * plain row per run, newest first, saying when it started, how long it took
 * and how it ended. While a finished run waits for the user's check, its row
 * asks for *Accept result* or *Not done*. Contracts, operations and
 * verifications stay in the API for agents and Review.
 */
import { useEffect, useState } from "react";
import {
  getTaskWorkDetail,
  getTaskWorkTimeline,
  submitTaskWorkVerification,
  type TaskWorkDetail,
  type TaskWorkTimeline,
} from "@/api/task-work";
import { Button, PocketRow, PocketSection } from "@/components/ui";
import { formatDate, useT } from "@/i18n";
import { panelsTaskSessionMessages } from "@/i18n/messages/panels-task-session";

type Run = TaskWorkTimeline["executions"][number];
type Ending = "runRunning" | "runFinished" | "runFailed" | "runStopped";

const ENDING_BY_STATUS: Readonly<Record<string, Ending>> = {
  completed: "runFinished",
  failed: "runFailed",
  cancelled: "runStopped",
};

/** Outcomes the user already gave; the run no longer waits. */
const ANSWERED = new Set(["verified_complete", "verified_failed"]);

const MINUTE_MS = 60_000;

function runEnding(run: Run): Ending {
  return ENDING_BY_STATUS[run.status] ?? "runRunning";
}

/** Whole minutes from `start` to `end`, at least one. */
function minutesBetween(start: string, end: number): number | null {
  const from = Date.parse(start);
  if (Number.isNaN(from) || Number.isNaN(end)) return null;
  return Math.max(1, Math.round((end - from) / MINUTE_MS));
}

function RunRow({
  run,
  waiting,
  verifying,
  onVerify,
}: {
  run: Run;
  waiting: boolean;
  verifying: boolean;
  onVerify: (status: "passed" | "failed") => void;
}) {
  const t = useT(panelsTaskSessionMessages);
  const ending = runEnding(run);
  const start = run.started_at || run.created_at;
  const running = ending === "runRunning";
  const minutes = running
    ? run.started_at && minutesBetween(run.started_at, Date.now())
    : run.completed_at && minutesBetween(start, Date.parse(run.completed_at));
  const duration = minutes
    ? t(running ? "runDurationSoFar" : "runDuration", { minutes })
    : null;
  const words = duration ? `${duration} · ${t(ending)}` : t(ending);
  const tone = running ? "working" : ending === "runFailed" ? "error" : null;
  return (
    <PocketRow
      testId={`task-run-row-${run.id}`}
      title={
        <time dateTime={start}>
          {formatDate(start, {
            weekday: "short",
            day: "numeric",
            month: "short",
            hour: "2-digit",
            minute: "2-digit",
          })}
        </time>
      }
      secondLine={waiting ? t("runWaits") : undefined}
      trailing={tone ? undefined : words}
      status={tone ? { tone, text: words } : undefined}
      below={
        waiting ? (
          <>
            <Button
              size="sm"
              disabled={verifying}
              onClick={() => onVerify("passed")}
              data-testid="task-run-accept"
            >
              {t("acceptResult")}
            </Button>
            <Button
              variant="secondary"
              size="sm"
              disabled={verifying}
              onClick={() => onVerify("failed")}
              data-testid="task-run-not-done"
            >
              {t("notDone")}
            </Button>
          </>
        ) : undefined
      }
    />
  );
}

export function TaskRunsSection({
  projectId,
  taskId,
  refreshSignal,
}: {
  projectId: string;
  taskId: string;
  refreshSignal: number;
}) {
  const t = useT(panelsTaskSessionMessages);
  const [detail, setDetail] = useState<TaskWorkDetail | null>(null);
  const [runs, setRuns] = useState<readonly Run[]>([]);
  const [error, setError] = useState("");
  const [verifying, setVerifying] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setError("");
    void Promise.all([
      getTaskWorkDetail(projectId, taskId),
      getTaskWorkTimeline(projectId, taskId),
    ])
      .then(([nextDetail, timeline]) => {
        if (cancelled) return;
        setDetail(nextDetail);
        setRuns(timeline.executions);
      })
      .catch(() => {
        if (cancelled) return;
        setDetail(null);
        setRuns([]);
        setError(t("runsFailed"));
      });
    return () => {
      cancelled = true;
    };
  }, [projectId, taskId, refreshSignal, t]);

  const acceptance = detail?.contract?.criteria.find(
    (criterion) => criterion.verification_kind === "user_acceptance",
  );
  const latest = runs[0];
  const waitingRunId =
    acceptance &&
    detail?.execution_status === "completed" &&
    !ANSWERED.has(detail.verified_outcome) &&
    latest?.status === "completed"
      ? latest.id
      : null;

  async function verify(status: "passed" | "failed") {
    if (!acceptance) return;
    setVerifying(true);
    setError("");
    try {
      const result = await submitTaskWorkVerification(projectId, taskId, {
        criterion_id: acceptance.id,
        status,
      });
      setDetail((current) =>
        current
          ? { ...current, verified_outcome: result.outcome_state }
          : current,
      );
    } catch {
      setError(t("reviewSaveFailed"));
    } finally {
      setVerifying(false);
    }
  }

  if (runs.length === 0 && !error) return null;
  return (
    <PocketSection title={t("runs")} testId="task-runs-section">
      {runs.map((run) => (
        <RunRow
          key={run.id}
          run={run}
          waiting={run.id === waitingRunId}
          verifying={verifying}
          onVerify={(status) => void verify(status)}
        />
      ))}
      {error ? (
        <p
          className="px-2 py-1.5 text-appkit-small font-medium text-danger"
          data-testid="task-runs-error"
        >
          {error}
        </p>
      ) : null}
    </PocketSection>
  );
}
